import { ForwardedRef, forwardRef, ReactNode, useEffect, useRef, useState } from 'react';
import { UseFormProps, UseFormReturn, useForm, WatchObserver } from 'react-hook-form';
import { useText } from '../../../utils/hooks/use_text';
import { CellulaFormError } from './_form_error.component';
import { CellulaFormSubmitButton } from './_form_submit_button.component';
import { CellulaFormSuccess } from './_form_success.component';
import { FormContext } from './form.context';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
type ResultLike<T = any, E = any> = { ok: T; err: null } | { ok: null; err: E };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
export type CellulaFormSubmitData = { [x: string]: any };
// eslint-disable-next-line @typescript-eslint/no-explicit-any
// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
export type CellulaFormInvalidFieldsData = { [field: string]: any };
export type CellulaFormControl = UseFormReturn;
export type CellulaFormSubmitInteractions = boolean | void | ResultLike;
export type CellulaFormOnSubmitReturnType = Promise<CellulaFormSubmitInteractions> | CellulaFormSubmitInteractions;

interface FormProps {
	options: UseFormProps;
	children: ReactNode;
	onSubmit: (data: CellulaFormSubmitData, control: CellulaFormControl) => CellulaFormOnSubmitReturnType;
	onAfterSubmit?: (data: CellulaFormSubmitData, control: CellulaFormControl) => void;
	onInvalid?: (errors: CellulaFormInvalidFieldsData) => void;
	onInputChange?: WatchObserver<CellulaFormSubmitData>;
	disabledSubmitButtonWhenClean?: boolean;
	disabledSubmitButtonWhenInvalid?: boolean;
	allowSubmitEventPropagation?: boolean;
}

const CellulaForm = forwardRef<HTMLFormElement, FormProps>(function CellulaForm(
	{
		options,
		onSubmit,
		onAfterSubmit,
		onInvalid,
		onInputChange,
		children,
		disabledSubmitButtonWhenClean = false,
		disabledSubmitButtonWhenInvalid = false,
		allowSubmitEventPropagation = false,
	}: FormProps,
	ref: ForwardedRef<HTMLFormElement>,
): JSX.Element {
	const [successMessage, setSuccessMessage] = useState<null | string>(null);
	const [submitError, setSubmitError] = useState<boolean | string>(false);
	const control = useForm(options);
	const staticTexts = useText();
	const isSubmittingRef = useRef(false);

	useEffect(() => {
		if (onInputChange) {
			const dispatch = control.watch((data, info) => {
				onInputChange(data, info);
			});

			return () => {
				dispatch.unsubscribe();
			};
		}
	}, [onInputChange, control]);

	async function setError(someError: unknown): Promise<void> {
		let someUnwrappedValue: unknown = someError;

		if (isFunction(someUnwrappedValue)) {
			someUnwrappedValue = someUnwrappedValue();
		}

		if (isPromise(someUnwrappedValue)) {
			someUnwrappedValue = await someUnwrappedValue;
		}

		if (isResultType(someUnwrappedValue)) {
			someUnwrappedValue = someUnwrappedValue.err;
		}

		if (isError(someUnwrappedValue)) {
			someUnwrappedValue = someUnwrappedValue.message;
		}

		if (isString(someUnwrappedValue)) {
			setSubmitError(someUnwrappedValue);
		} else {
			setSubmitError(staticTexts('Component.Form.UnknownSubmitError'));
		}
	}

	async function handleInvalid(errors: CellulaFormInvalidFieldsData): Promise<void> {
		onInvalid?.(errors);
	}

	async function handleSubmit(data: CellulaFormSubmitData): Promise<void> {
		// A user should only be able to preform an actual submit form if changes
		// has been made.
		if (disabledSubmitButtonWhenClean && !control.formState.isDirty) return;

		setSubmitError(false);
		setSuccessMessage(null);

		try {
			const someValue = onSubmit(data, control);
			let someUnwrappedValue: unknown = someValue;

			if (isPromise(someUnwrappedValue)) {
				someUnwrappedValue = await someUnwrappedValue;
			}

			if (isResultType(someUnwrappedValue)) {
				if (someUnwrappedValue.err) {
					await setError(someUnwrappedValue.err);

					return;
				}

				someUnwrappedValue = someUnwrappedValue.ok;
			}

			if (isBoolean(someUnwrappedValue)) {
				// If the result of all this discovery work is a falsy boolean value,
				// we should not reset the form state.
				if (!someUnwrappedValue) {
					return;
				}
			}

			if (isString(someUnwrappedValue)) {
				setSuccessMessage(someUnwrappedValue);
			}

			// Reset form state with newly submitted data
			control.reset(data);

			onAfterSubmit?.(data, control);
		} catch (error) {
			await setError(error);
		}
	}

	function handleSubmitWrapper(event: React.FormEvent<HTMLFormElement>): Promise<void> {
		// Don't propagate submit event to make it possible to have nested forms. By
		// default we want to prevent the propagation of the submit event but it can
		// be opted out by props
		if (!allowSubmitEventPropagation) {
			event.preventDefault();
			event.stopPropagation();
		}

		// The submit button is only marked as busy while submitting, it is never
		// natively disabled, so the browser still triggers a native form submission
		// on a rapid second click. Block re-entrant submissions to avoid firing
		// competing requests.
		if (isSubmittingRef.current) {
			event.preventDefault();

			return Promise.resolve();
		}

		isSubmittingRef.current = true;

		const submission = control.handleSubmit(handleSubmit, handleInvalid)(event);

		return submission.finally(() => {
			isSubmittingRef.current = false;
		});
	}

	return (
		<FormContext.Provider
			value={{
				control,
				options,
				submitError,
				setSubmitError,
				disabledSubmitButtonWhenClean,
				disabledSubmitButtonWhenInvalid,
				successMessage,
			}}
		>
			<form style={{ display: 'contents' }} ref={ref} onSubmit={handleSubmitWrapper} noValidate>
				{children}
			</form>
		</FormContext.Provider>
	);
});

function isResultType(value: unknown): value is { ok: unknown; err: unknown } {
	if (typeof value === 'object' && !Array.isArray(value) && value !== null && 'ok' in value && 'err' in value) {
		return true;
	}

	return false;
}

function isPromise(value: unknown): value is Promise<unknown> {
	return typeof value === 'object' && !Array.isArray(value) && value !== null && 'then' in value;
}

function isBoolean(value: unknown): value is boolean {
	return typeof value === 'boolean';
}

function isString(value: unknown): value is string {
	return typeof value === 'string';
}

function isError(value: unknown): value is Error {
	return value instanceof Error;
}

// eslint-disable-next-line @typescript-eslint/ban-types
// biome-ignore lint/complexity/noBannedTypes: suppressed due to migration - should be fixed
function isFunction(value: unknown): value is Function {
	return typeof value === 'function';
}

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

/**
 * Form handling
 */
export const Form = {
	Root: CellulaForm,
	Success: CellulaFormSuccess,
	Error: CellulaFormError,
	SubmitButton: CellulaFormSubmitButton,
} as const;
export type { FormProps };
